/**
 * Shared button with design-system variants (`type`), optional icon/text, and loading state.
 *
 * @param {object} props
 * @param {import('react').ReactNode} [props.icon]
 * @param {string} [props.text]
 * @param {string} [props.type] - Visual variant (primary, secondary, warning, danger, etc.)
 * @param {string} [props.className]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.loading]
 * @param {import('react').Ref} [props.ref]
 * @param {string} [props.title]
 * @param {string} [props.dataTestId]
 * @param {string} [props.'data-testid']
 * @param {() => void} [props.onClick]
 */
const Button = ({
  icon,
  text,
  type,
  className,
  disabled,
  onClick,
  ref,
  title,
  loading,
  dataTestId,
  'data-testid': dataTestIdProp,
  ...props
}) => {
  const baseTestId = dataTestIdProp || dataTestId || 'button-component'

  const buttonClass = () => {
    if (type === 'primary-shipment-view') {
      return `bg-Button-Focus-brand-secondary text-Misc-brand hover:bg-BG-Hover-brand-00 border 
            border-Border-Selected-brand-01 hover:text-Icon-on-color ${
              loading ? 'bg-BG-Hover-brand-00' : ''
            }`
    }
    if (type === 'primary') {
      return `bg-Button-Default-brand-primary text-Text-on-color hover:bg-Button-Hover-brand-primary ${
        loading ? 'bg-Button-Hover-brand-primary' : ''
      }`
    } else if (type === 'primary-track') {
      // Public /track pages (FE-1354): primary with pressed state, focus-visible ring,
      // and the handoff's 120ms background-only transition. Sizing comes from className.
      return `bg-Button-Default-brand-primary text-Text-on-color transition-colors duration-[120ms] hover:bg-Button-Hover-brand-primary active:bg-Button-Focus-brand-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-Misc-brand ${
        loading ? 'bg-Button-Hover-brand-primary' : ''
      }`
    } else if (type === 'primary-v2') {
      return `bg-BG-Default-white text-Button-Default-brand-secondary hover:bg-Button-Hover-brand-secondary hover:text-Text-on-color border border-Button-Default-brand-secondary hover:border-Button-Hover-brand-secondary ${
        loading ? 'bg-Button-Hover-brand-secondary' : ''
      }`
    } else if (type === 'primary-v3') {
      return `text-Button-Default-brand-secondary hover:text-Text-on-color hover:bg-Button-Hover-brand-secondary border border-transparent ${
        loading ? 'bg-Button-Hover-brand-secondary' : ''
      }`
    } else if (type === 'secondary') {
      return `text-Text-large-08 hover:text-Button-Default-brand-secondary hover:bg-BG-Default-neutral-00 border border-transparent ${
        loading ? 'bg-BG-Default-neutral-00' : ''
      }`
    } else if (type === 'secondary-v2') {
      return `text-Misc-brand hover:bg-BG-Default-neutral-00 border border-transparent ${
        loading ? 'bg-BG-Default-neutral-00' : ''
      }`
    } else if (type === 'secondary-v3') {
      return `bg-BG-Default-white text-Button-Default-brand-secondary border border-Button-Default-brand-secondary hover:border-Button-Hover-brand-secondary hover:text-Text-on-color hover:bg-Button-Hover-brand-secondary ${
        loading ? 'bg-BG-Default-neutral-00' : ''
      }`
    } else if (type === 'tertiary') {
      return `text-Text-on-color bg-Button-Default-alternative hover:bg-Button-Hover-alternative border border-Border-Default-alternative hover:border-Border-hover-alternative ${
        loading ? 'bg-Button-Hover-alternative' : ''
      }`
    } else if (type === 'tertiary-v2') {
      return `text-Button-Default-alternative border border-Border-Default-alternative hover:border-Border-hover-alternative ${
        loading ? 'border-Border-Hover-alternative' : ''
      }`
    } else if (type === 'warning') {
      return `bg-BG-Default-critical-00 text-Text-large-08 hover:text-Text-on-color hover:bg-Button-Hover-danger-primary border border-Border-Default-critical-03 ${
        loading ? 'bg-Button-Hover-danger-primary' : ''
      }`
    } else if (type === 'warning-v2') {
      return `bg-Button-Default-danger-primary text-Text-on-color hover:text-Text-on-color hover:bg-Button-Hover-danger-primary border border-Border-Default-danger-primary hover:border-Border-hover-danger-primary ${
        loading ? 'bg-Button-Hover-danger-primary' : ''
      }`
    } else if (type === 'danger') {
      return `bg-Button-Default-white text-Text-critical hover:text-Text-on-color hover:bg-Button-Hover-danger-primary border border-Border-Default-danger-primary hover:border-Border-hover-warning-primary ${
        loading ? 'bg-Button-Hover-warning-primary' : ''
      }`
    } else {
      return ''
    }
  }

  return (
    <button
      ref={ref || null}
      className={`${
        disabled
          ? type === 'warning-v2'
            ? `${buttonClass()} text-Text-on-color bg-Button-Hover-danger-primary opacity-50 cursor-not-allowed`
            : 'text-Text-placeholder bg-BG-Default-neutral-01 cursor-not-allowed'
          : buttonClass()
      } ${className || ''} rounded flex items-center justify-center gap-2 group`}
      disabled={disabled || loading}
      onClick={onClick}
      title={title && title}
      {...props}
      data-testid={baseTestId}
    >
      {loading ? <span className="spin" data-testid={`${baseTestId}-spinner`}></span> : null}
      {icon && !loading ? <span data-testid={`${baseTestId}-icon`}>{icon}</span> : null}
      {text && !loading ? <span data-testid={`${baseTestId}-text`}>{text}</span> : null}
    </button>
  )
}

export default Button
